import {
  getIsLinkExternal,
  getLinkElement,
  getLinkHrefAndRel,
  getPotentialLinkMailtoHref,
} from '@/lib/link-config';
import { cn } from '@/lib/utils';

import Button from '@/components/atoms/Button';
import { SvgIcon } from '@/components/SvgIcon';

import { HTMLDataTrackerProps, PHTrackerProps } from '@/app/types';
import { WithRef } from '@/cms/types/types';

import { AhrefElement, NextLinkElement } from '../Button/ButtonContent';
import ButtonIcon from '../Button/ButtonIcon';
import {
  animatedIconLeftStyles,
  animatedIconRightStyles,
} from '../Button/styles';

import iconChevronLeftSrc from '~/svg/IconChevronLeft.svg';
import iconChevronRightSrc from '~/svg/IconChevronRight.svg';
import iconExternalSrc from '~/svg/IconExternal.svg';

export type LinkButtonInnerProps = {
  showLeftChevron?: boolean;
  showRightChevron?: boolean;
  showExternalIcon?: boolean;
  underline?: boolean;
  href: string;
  openInNewTab?: boolean;
  id?: number;
  isDecoro?: boolean;
};

export type LinkButtonProps = (AhrefElement | NextLinkElement) &
  LinkButtonInnerProps &
  HTMLDataTrackerProps &
  PHTrackerProps;

const LinkButton = ({
  children,
  variant = 'filled',
  slim = false,
  href,
  leftSection,
  rightSection,
  classNames,
  showLeftChevron,
  showRightChevron,
  showExternalIcon,
  openInNewTab,
  underline = true,
  ref,
  rel: relProp,
  ...props
}: WithRef<HTMLAnchorElement, LinkButtonProps>) => {
  if (!href) {
    console.log('LinkButton: href is not provided');
    return null;
  }

  const hasExplicitRel = relProp !== undefined;
  const { href: resolvedHref, rel } = getLinkHrefAndRel(href, {
    hasRel: hasExplicitRel,
    rel: relProp,
  });
  const customHref = getPotentialLinkMailtoHref(resolvedHref);
  const isExternal = getIsLinkExternal(resolvedHref);
  const element = getLinkElement(resolvedHref);

  const shouldRenderLeftSection = showLeftChevron || leftSection;
  const extraLeftSection = shouldRenderLeftSection && (
    <>
      {showLeftChevron && <SvgIcon src={iconChevronLeftSrc} />}
      {leftSection && <ButtonIcon>{leftSection}</ButtonIcon>}
    </>
  );

  const shouldRenderRightSection =
    rightSection || showExternalIcon || showRightChevron;

  const extraRightSection = shouldRenderRightSection && (
    <>
      {rightSection && <ButtonIcon>{rightSection}</ButtonIcon>}
      {showExternalIcon && <SvgIcon src={iconExternalSrc} />}
      {showRightChevron && <SvgIcon src={iconChevronRightSrc} />}
    </>
  );

  const innerOpenInNewTab =
    (isExternal && (openInNewTab === undefined || !!openInNewTab)) ||
    openInNewTab
      ? '_blank'
      : '_self';

  return (
    <Button
      ref={ref}
      as={element}
      target={innerOpenInNewTab}
      href={customHref}
      variant={variant}
      slim={slim}
      classNames={{
        root: cn(
          'disabled:text-gray-500',
          variant === 'text' && underline && 'hover:underline',
          variant === 'text' &&
            'font-mono hover:bg-transparent active:bg-transparent font-normal',
          (showRightChevron || showLeftChevron) && 'group',
          classNames?.root
        ),
        leftSection: cn(
          showLeftChevron && animatedIconLeftStyles,
          classNames?.leftSection
        ),
        rightSection: cn(
          showRightChevron && animatedIconRightStyles,
          classNames?.rightSection
        ),
        inner: classNames?.inner,
      }}
      leftSection={extraLeftSection}
      rightSection={extraRightSection}
      {...props}
      rel={rel}
    >
      {children}
    </Button>
  );
};

export default LinkButton;
